import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/RSIDE.png';
import './Legal.css';

const TOC = [
  { id: 'glance',        label: 'Privacy at a Glance' },
  { id: 's1',            label: '1. Scope' },
  { id: 's2',            label: '2. Privacy Roles' },
  { id: 's3',            label: '3. Information We Collect' },
  { id: 's4',            label: '4. Sources' },
  { id: 's5',            label: '5. How We Use Information' },
  { id: 's6',            label: '6. Lawful Bases (EEA/UK)' },
  { id: 's7',            label: '7. How We Disclose' },
  { id: 's8',            label: '8. Sale & Advertising' },
  { id: 's9',            label: '9. Cookies & Technologies' },
  { id: 's10',           label: '10. Data Retention' },
  { id: 's11',           label: '11. Data Security' },
  { id: 's12',           label: '12. International Transfers' },
  { id: 's13',           label: '13. Privacy Rights' },
  { id: 's14',           label: '14. Account & Choices' },
  { id: 's15',           label: '15. California Residents' },
  { id: 's16',           label: '16. EEA & UK' },
  { id: 's17',           label: '17. Tenants Without Accounts' },
  { id: 's18',           label: '18. Children Under 18' },
  { id: 's19',           label: '19. Third-Party Services' },
  { id: 's20',           label: '20. Policy Changes' },
  { id: 's21',           label: '21. Contact Us' },
];

function PrivacyPolicy() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="legal-root">
      <nav className="legal-nav">
        <img src={logo} alt="RSIDE" className="legal-logo" onClick={() => navigate('/')} />
        <button className="legal-back-btn" onClick={() => navigate('/')}>← Back to Home</button>
      </nav>

      <div className="legal-outer">

        <div className="legal-header">
          <p className="legal-company-label">DTecX LLC | Rside / Rside-Tenant</p>
          <h1 className="legal-title">Privacy Policy</h1>
          <div className="legal-meta-row">
            <span><strong>Effective Date:</strong> September 5, 2026</span>
            <span><strong>Last Updated:</strong> September 5, 2026</span>
          </div>
        </div>

        <div className="legal-layout">

          {/* Sticky TOC */}
          <aside className="legal-toc">
            <div className="legal-toc-title">Contents</div>
            {TOC.map(({ id, label }) => (
              <button
                key={id}
                className={`legal-toc-link${activeId === id ? ' active' : ''}`}
                onClick={() => scrollTo(id)}
              >
                {label}
              </button>
            ))}
          </aside>

          {/* Main content */}
          <div className="legal-body">

            <p>
              DTecX LLC ("DTecX," "Rside," "Rside-Tenant," "we," "us," or "our") respects privacy and is
              committed to explaining how personal information is collected, used, disclosed, stored, and
              otherwise processed in connection with the Rside property-management platform, Rside-Tenant
              platform, websites, mobile applications, related application programming interfaces used to
              provide the Services, and associated features (collectively, the "Services").
            </p>
            <p>
              This Privacy Policy applies to Manager Users, Tenant Users, website and app visitors, and
              individuals whose personal information is provided to Rside by an authorized Manager User,
              including tenants who have not yet created an account.
            </p>
            <p>
              This Privacy Policy is separate from the Rside and Rside-Tenant Terms and Conditions. The
              Terms govern contractual use of the Services; this Privacy Policy explains DTecX's privacy
              practices.
            </p>
            <p>
              If you do not agree with this Privacy Policy, do not provide personal information to the
              Services or use the Services where such processing is not necessary for an existing legal
              or contractual relationship.
            </p>

            {/* ── Privacy at a Glance ── */}
            <h2 id="glance" className="legal-section">Privacy at a Glance</h2>
            <div className="legal-table-wrap">
              <table className="legal-table">
                <thead>
                  <tr><th>Topic</th><th>Summary</th></tr>
                </thead>
                <tbody>
                  <tr><td>Who operates Rside?</td><td>DTecX LLC.</td></tr>
                  <tr><td>Who uses the Services?</td><td>Property owners/managers and invited tenants; the Services are intended for users age 18 or older.</td></tr>
                  <tr><td>What information is handled?</td><td>Account/contact data, property and tenant data, leases and documents, work-order information, communications, payment/transaction data, connected financial-account data, subscription data, and technical/service data.</td></tr>
                  <tr><td>Where can tenant data come from?</td><td>Directly from the tenant or from a Manager User before the tenant creates an account.</td></tr>
                  <tr><td>Payments</td><td>Stripe processes rent-payment transactions and connected-account payment functionality. Rside may receive transaction and status information needed to provide payment features.</td></tr>
                  <tr><td>Bank imports</td><td>Manager Users may connect financial accounts through Plaid to import transaction information.</td></tr>
                  <tr><td>AI</td><td>Lease or document content may be sent to Anthropic or another disclosed AI provider for extraction. Users must verify extracted information.</td></tr>
                  <tr><td>Document sharing</td><td>Manager-uploaded documents may be shared with tenants according to the selected setting. Tenant-uploaded documents shall be automatically shared with the associated Manager User.</td></tr>
                  <tr><td>Retention</td><td>Information is kept for as long as reasonably needed for the Services, legal/security purposes, disputes, and backup/recovery needs. No single retention period applies to every category.</td></tr>
                  <tr><td>Privacy requests</td><td>Depending on location, individuals may have rights to access, correct, delete, restrict, object, port, or otherwise control personal information. Requests may be sent to support@dtecx.net.</td></tr>
                </tbody>
              </table>
            </div>

            {/* ── 1 ── */}
            <h2 id="s1" className="legal-section">1. Scope</h2>
            <p>
              This Privacy Policy applies when you visit or use the Rside or Rside-Tenant websites and
              applications, create or use an account, communicate through the Services, upload or receive
              documents, use payment or bank-import functionality, use AI-assisted extraction, receive
              notifications, contact support, or otherwise interact with DTecX in connection with the Services.
            </p>
            <p>
              It also applies when a Manager User provides personal information about a tenant, prospective
              tenant, past tenant, service provider, or other individual in connection with
              property-management activities, even if that individual has not created a Rside account.
            </p>
            <p>
              This Privacy Policy does not replace privacy notices or legal obligations that may
              independently apply to a landlord, property owner, property manager, employer, service
              provider, financial institution, or other third party.
            </p>

            {/* ── 2 ── */}
            <h2 id="s2" className="legal-section">2. Privacy Roles and Important Definitions</h2>
            <p>
              Terms such as "Manager User," "Tenant User," "User Content," and "Services" have the meanings
              given in the Rside and Rside-Tenant Terms and Conditions unless this Privacy Policy states
              otherwise.
            </p>
            <p>
              Depending on the processing activity, DTecX may determine the purposes and means of processing
              personal information for its own account, security, subscription, support,
              platform-operation, and legal-compliance activities. For certain information that a Manager
              User uploads or manages on behalf of tenants or other individuals, the Manager User may
              independently determine why that information is collected and used, and DTecX may process
              the information to provide the Services on the Manager User's behalf.
            </p>
            <p>
              The exact legal classification of DTecX and a Manager User may vary by jurisdiction and
              processing activity. Manager Users remain responsible for their own privacy notices, lawful
              bases, permissions, and other obligations that applicable law places on them.
            </p>

            {/* ── 3 ── */}
            <h2 id="s3" className="legal-section">3. Personal Information We Collect or Receive</h2>
            <p>
              The categories below describe information that may be collected, received, generated, or
              processed depending on which features are used.
            </p>

            <h3>3.1 Account and Contact Information</h3>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Account username or identifiers</li>
              <li>Password or authentication-related information handled through the Services and authentication providers</li>
              <li>Account role, such as Manager User or Tenant User</li>
              <li>Communication preferences</li>
              <li>Information submitted when contacting support</li>
            </ul>

            <h3>3.2 Property, Lease, and Tenant Information</h3>
            <ul>
              <li>Property and unit addresses and identifying information</li>
              <li>Tenant names and contact details</li>
              <li>Lease dates, rent amounts, due dates, fees, and other lease-related data</li>
              <li>Occupancy and tenant-history information</li>
              <li>Property details, assets, maintenance information, and service-provider records</li>
              <li>Other property-management information entered by a Manager User</li>
            </ul>
            <p>
              A Manager User may provide tenant information before the tenant creates a Rside-Tenant
              account. DTecX receives that information from the Manager User rather than directly from
              the tenant in those circumstances.
            </p>

            <h3>3.3 User Content, Documents, Images, and Communications</h3>
            <ul>
              <li>Lease PDFs and other lease documents</li>
              <li>Other uploaded documents</li>
              <li>Work-order photographs and information</li>
              <li>Receipts and financial records</li>
              <li>Insurance-related documents</li>
              <li>Tenant maintenance documents</li>
              <li>Announcements, comments, and communications sent through the Services</li>
              <li>Other files or content voluntarily submitted through available features</li>
            </ul>

            <h3>3.4 Rent-Payment and Stripe Information</h3>
            <p>
              Rside-Tenant may allow Tenant Users to initiate Rent Payments through Stripe and Stripe
              Connect. Stripe provides the underlying payment-processing infrastructure. DTecX may receive
              or maintain information necessary to display and administer the payment feature, such as
              payment amounts, transaction identifiers, payment status, payment-method type,
              connected-account identifiers, timing information, dispute or return status, and related
              records.
            </p>
            <p>
              Payment credentials and bank/card information may be processed directly by Stripe or other
              financial-service providers. Users should review the applicable third-party privacy notices
              for information about how those providers handle payment data.
            </p>

            <h3>3.5 Financial-Account and Plaid Information</h3>
            <p>
              Manager Users may connect financial accounts through Plaid for transaction imports.
              Depending on the connection and financial institution, Rside may receive
              financial-account and transaction information that Plaid makes available to the Services,
              including transaction records and account-related information needed to provide the import
              feature.
            </p>
            <p>
              Plaid functionality is currently a Manager User feature and is not used by Tenant Users
              to connect their bank accounts through Rside.
            </p>

            <h3>3.6 Subscription and Billing Information</h3>
            <ul>
              <li>Subscription plan and billing interval</li>
              <li>Trial status and renewal information</li>
              <li>Billing status and payment success or failure information</li>
              <li>Subscription transaction identifiers</li>
              <li>Information needed to process cancellations, refunds, credits, or support requests</li>
            </ul>

            <h3>3.7 AI-Assisted Document Processing Information</h3>
            <p>
              When a User uses AI-assisted lease or document extraction, relevant document content may
              be transmitted to and processed by Anthropic or another AI provider used by DTecX to
              perform the requested extraction. This may include personal information contained in the
              document, such as names, addresses, dates, rent amounts, fees, and other lease terms.
            </p>
            <p>
              AI extraction is intended to assist with data entry and review. Rside does not rely on
              the extraction feature to make a solely automated decision that determines whether a
              tenant is approved, evicted, charged rent, or otherwise subjected to a legal or similarly
              significant decision by DTecX.
            </p>

            <h3>3.8 Device, Usage, Security, and Technical Information</h3>
            <p>
              When the Services are accessed, DTecX and its technology providers may process technical
              information necessary to operate, secure, authenticate, troubleshoot, and deliver the
              Services. Depending on the platform and configuration, this may include IP address, device
              or browser type, operating-system information, app version, authentication events,
              timestamps, request or server logs, session information, notification tokens, and similar
              technical data.
            </p>

            <h3>3.9 Location and Mapping Information</h3>
            <p>
              The Services may process property addresses and other location information entered by
              Users in connection with property-management features and mapping functionality. If a
              mobile feature requests device location, access will be subject to the permissions made
              available by the applicable device or operating system.
            </p>

            <h3>3.10 Marketing and Communication Information</h3>
            <p>
              DTecX may process email addresses, communication preferences, and engagement information
              reasonably necessary to send service-related communications and, where permitted,
              marketing communications. Users may opt out of marketing email as described below.
            </p>

            {/* ── 4 ── */}
            <h2 id="s4" className="legal-section">4. Sources of Personal Information</h2>
            <p>DTecX may obtain personal information from the following sources:</p>
            <ul>
              <li>Directly from you when you register, enter information, upload documents, use features, or contact support</li>
              <li>From a Manager User who enters tenant or property-related information before or after a tenant creates an account</li>
              <li>From a Tenant User who uploads information or documents that are shared with the associated Manager User</li>
              <li>From Stripe in connection with Rent Payments, connected accounts, subscription billing, transaction status, disputes, returns, and related payment functionality</li>
              <li>From Plaid and connected financial institutions when a Manager User authorizes a financial-account connection</li>
              <li>From Firebase, Apple, Microsoft, Mapbox, DigitalOcean, Anthropic, and other technology providers used to operate particular Services</li>
              <li>From devices, browsers, apps, servers, and network interactions when the Services are used</li>
              <li>From other sources at your direction or as permitted by law</li>
            </ul>

            {/* ── 5 ── */}
            <h2 id="s5" className="legal-section">5. How We Use Personal Information</h2>
            <p>DTecX may use personal information for the following purposes, as applicable:</p>
            <ul>
              <li>To create, authenticate, administer, secure, and support accounts</li>
              <li>To provide property, unit, tenant, lease, document, work-order, maintenance, financial, and related property-management features</li>
              <li>To display and maintain records entered by Manager Users and Tenant Users</li>
              <li>To facilitate document storage and document sharing according to the applicable sharing settings</li>
              <li>To enable Rent Payments and display payment status information through Stripe</li>
              <li>To enable Manager Users to connect financial accounts and import transactions through Plaid</li>
              <li>To perform AI-assisted lease or document extraction requested by a User</li>
              <li>To send account, security, billing, payment, rent, work-order, legal, announcement, and other service communications</li>
              <li>To provide customer support and respond to inquiries</li>
              <li>To administer subscriptions, trials, renewals, cancellations, refunds, and billing support</li>
              <li>To prevent, detect, investigate, and respond to fraud, abuse, unauthorized access, security threats, and violations of the Terms</li>
              <li>To maintain, troubleshoot, test, improve, and develop the Services</li>
              <li>To comply with law, legal process, lawful requests, and regulatory obligations</li>
              <li>To establish, exercise, or defend legal claims and enforce agreements</li>
              <li>For other purposes disclosed at the time of collection or with your direction or consent where required</li>
            </ul>

            {/* ── 6 ── */}
            <h2 id="s6" className="legal-section">6. Lawful Bases for Processing in the EEA and UK</h2>
            <p>
              If European Economic Area or United Kingdom data-protection law applies to a processing
              activity for which DTecX is the controller, DTecX may rely on one or more lawful bases
              depending on the context:
            </p>
            <ul>
              <li><strong>Contract or pre-contract steps:</strong> where processing is necessary to create or administer an account, provide requested Services, process a subscription, or perform other obligations to a User</li>
              <li><strong>Legitimate interests:</strong> where processing is reasonably necessary to operate, secure, support, improve, prevent fraud against, and protect the Services or Users, provided those interests are not overridden by applicable individual rights and freedoms</li>
              <li><strong>Legal obligation:</strong> where processing is necessary to comply with applicable legal, tax, accounting, regulatory, or law-enforcement obligations</li>
              <li><strong>Consent:</strong> where applicable law requires consent, including for certain marketing communications, optional device permissions, or non-essential tracking technologies</li>
            </ul>
            <p>
              When a Manager User determines the purposes of processing tenant or third-party information
              and DTecX processes that information on the Manager User's behalf, the Manager User is
              responsible for determining and documenting the appropriate lawful basis unless applicable
              law provides otherwise.
            </p>

            {/* ── 7 ── */}
            <h2 id="s7" className="legal-section">7. How We Disclose Personal Information</h2>
            <p>
              DTecX may disclose personal information as described below. Disclosure does not necessarily
              mean that the information is "sold" or "shared" as those terms are specifically defined
              under any particular privacy law.
            </p>

            <h3>7.1 Between Manager Users and Tenant Users</h3>
            <p>
              Rside supports information sharing between Manager Users and Tenant Users.
              Manager-uploaded documents may be shared with the associated Tenant User when the Manager
              User enables the applicable sharing setting. Where supported, that setting may later be
              changed.
            </p>
            <p>
              Documents uploaded by a Tenant User shall be automatically shared with the associated
              Manager User. Tenant Users should not upload a document unless they intend for the
              associated Manager User to receive it.
            </p>

            <h3>7.2 Payment and Financial-Service Providers</h3>
            <p>
              Information may be disclosed to Stripe, Plaid, financial institutions, payment networks,
              connected-account participants, and related providers as necessary to provide payment,
              payout, financial-account connection, transaction-import, dispute, return, and other
              financial functionality requested by Users.
            </p>

            <h3>7.3 Technology and Service Providers</h3>
            <p>
              DTecX may disclose information to vendors and service providers that support the Services.
              Current or potential providers include Stripe, Plaid, Firebase, Apple, DigitalOcean,
              Anthropic, Microsoft, Mapbox, and other technology providers. These providers may process
              information under their own terms and privacy notices when they independently determine
              processing, or under contractual restrictions when they process information on DTecX's
              behalf.
            </p>

            <h3>7.4 Legal, Security, and Protection Purposes</h3>
            <p>
              DTecX may disclose information when reasonably necessary to comply with applicable law or
              legal process, respond to lawful government requests, protect the rights or safety of
              Users or others, investigate fraud or security incidents, enforce the Terms, or establish,
              exercise, or defend legal claims.
            </p>

            <h3>7.5 Business Transfers</h3>
            <p>
              Personal information may be disclosed or transferred as part of or in preparation for a
              merger, acquisition, financing, reorganization, sale of assets, bankruptcy, transfer of
              the Services, or similar corporate transaction, subject to applicable law.
            </p>

            <h3>7.6 At Your Direction or With Consent</h3>
            <p>
              DTecX may disclose information when a User directs the disclosure, requests an
              integration, chooses to share information with another User, or otherwise consents to
              the disclosure where consent is required.
            </p>

            {/* ── 8 ── */}
            <h2 id="s8" className="legal-section">8. Sale, Targeted Advertising, and Cross-Context Behavioral Advertising</h2>
            <p>
              The current Rside product does not use personal information for cross-context behavioral
              advertising. DTecX does not sell personal information for monetary or other valuable
              consideration and does not share personal information for cross-context behavioral
              advertising as those terms are defined under applicable U.S. state privacy laws.
            </p>

            {/* ── 9 ── */}
            <h2 id="s9" className="legal-section">9. Cookies, Local Storage, SDKs, and Similar Technologies</h2>
            <p>
              Websites and applications may use cookies, local storage, software development kits
              (SDKs), session technologies, authentication tokens, and similar technologies that are
              necessary or useful for account login, security, preferences, session continuity,
              notifications, and operation of the Services.
            </p>
            <p>
              If DTecX uses non-essential analytics, advertising, or similar tracking technologies in
              a jurisdiction that requires notice or consent, DTecX will provide the applicable notice
              and choice mechanism before or when those technologies are used.
            </p>
            <p>
              Browser and device settings may allow Users to control some cookies, local storage,
              notifications, and device permissions. Disabling technologies that are necessary for
              authentication or core functionality may prevent portions of the Services from working
              properly.
            </p>

            {/* ── 10 ── */}
            <h2 id="s10" className="legal-section">10. Data Retention</h2>
            <p>
              DTecX does not apply one universal retention period to every category of personal
              information. Personal information may be retained for as long as reasonably necessary
              for the purposes described in this Privacy Policy, including to provide and secure the
              Services, maintain account and transaction records, resolve disputes, enforce agreements,
              comply with legal obligations, and maintain appropriate business and security records.
            </p>
            <p>
              Retention decisions may consider the type and sensitivity of information, whether an
              account remains active, the relationship between a Manager User and Tenant User,
              contractual requirements, legal or regulatory obligations, limitation periods, fraud and
              security needs, and backup or disaster-recovery requirements.
            </p>
            <p>
              When supported content is deleted, deletion is generally intended to remove that content
              from the active Service promptly, and deleted content may not be recoverable through the
              ordinary application. Technical records, metadata, logs, database backups,
              disaster-recovery copies, or system restoration processes may temporarily retain or
              restore certain information.
            </p>
            <p>
              Rside is not intended to be a User's sole permanent archive or backup system. Users
              should maintain independent copies of important leases, notices, financial records,
              receipts, photographs, insurance documents, and other important files.
            </p>

            {/* ── 11 ── */}
            <h2 id="s11" className="legal-section">11. Data Security</h2>
            <p>
              DTecX uses administrative, technical, and organizational measures designed to protect
              personal information against unauthorized access, misuse, alteration, loss, or disclosure
              appropriate to the nature of the Services and the information processed.
            </p>
            <p>
              No method of electronic transmission, storage, authentication, or security is completely
              secure. DTecX therefore cannot guarantee that personal information will never be accessed,
              acquired, disclosed, altered, or destroyed without authorization.
            </p>
            <p>
              Users are responsible for protecting their account credentials, using accurate account
              information, and promptly notifying DTecX of suspected unauthorized account access or
              credential compromise.
            </p>

            {/* ── 12 ── */}
            <h2 id="s12" className="legal-section">12. International Processing and Transfers</h2>
            <p>
              DTecX is a United States company and the Services may be available to Users and
              Properties outside the United States. Personal information may therefore be processed in
              the United States and in other countries where DTecX or its service providers operate.
            </p>
            <p>
              Privacy and data-protection laws in those countries may differ from the laws where an
              individual resides. Where applicable law requires a specific mechanism or safeguard for
              an international transfer, DTecX will use an appropriate lawful mechanism or otherwise
              restrict the processing as required.
            </p>
            <p>
              Users outside the United States should understand that using the Services may involve
              international processing of their information, including by service providers used for
              hosting, authentication, payments, banking connections, mapping, communications, and
              AI-assisted extraction.
            </p>

            {/* ── 13 ── */}
            <h2 id="s13" className="legal-section">13. Privacy Rights and Choices</h2>
            <p>
              Depending on where you live and the nature of the processing, you may have some or all
              of the following rights:
            </p>
            <ul>
              <li>To request access to personal information about you</li>
              <li>To request correction of inaccurate personal information</li>
              <li>To request deletion of personal information, subject to legal and operational exceptions</li>
              <li>To request a portable copy of certain personal information</li>
              <li>To request restriction of certain processing</li>
              <li>To object to certain processing</li>
              <li>To withdraw consent where processing is based on consent, without affecting processing that occurred before withdrawal</li>
              <li>To opt out of certain sale, sharing, targeted-advertising, or profiling activities if applicable law provides such rights and DTecX engages in those activities</li>
              <li>To appeal certain privacy-request decisions where applicable law provides an appeal right</li>
              <li>To receive equal service and not be unlawfully discriminated against for exercising applicable privacy rights</li>
            </ul>
            <p>
              To make a privacy request, email{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a>{' '}
              with the subject line "Privacy Request" and describe the request and the account or
              information involved. DTecX may need to verify your identity and authority before
              completing certain requests.
            </p>
            <p>
              If an authorized agent submits a request on your behalf, DTecX may request information
              reasonably necessary to verify the agent's authority and your identity, subject to
              applicable law.
            </p>
            <p>
              Some information may be exempt from a request or retained where necessary to comply with
              law, complete transactions, provide requested Services, maintain security, prevent fraud,
              protect other individuals' rights, or establish, exercise, or defend legal claims.
            </p>

            {/* ── 14 ── */}
            <h2 id="s14" className="legal-section">14. Account, Communication, and Device Choices</h2>

            <h3>14.1 Account Information</h3>
            <p>
              Where account settings permit, Users may review or update certain account information
              directly in the Services. Users may also contact{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a>{' '}
              for assistance.
            </p>

            <h3>14.2 Marketing Email</h3>
            <p>
              Users may opt out of marketing email by using the unsubscribe mechanism included in the
              applicable message or by contacting support. Opting out of marketing does not prevent
              DTecX from sending transactional, account, security, payment, billing, legal, or other
              non-marketing communications where appropriate.
            </p>

            <h3>14.3 Push Notifications</h3>
            <p>
              Users may control push-notification permissions through the applicable device or
              operating-system settings. Disabling push notifications does not prevent information
              from remaining available within the Services and may not stop legally required or
              account-related communications sent by other methods.
            </p>

            <h3>14.4 Document-Sharing Controls</h3>
            <p>
              Manager Users are responsible for reviewing and selecting the appropriate
              document-sharing setting when the feature provides a sharing choice. Tenant-uploaded
              documents may be automatically shared with the associated Manager User as described
              above.
            </p>

            {/* ── 15 ── */}
            <h2 id="s15" className="legal-section">15. Additional Notice for California Residents</h2>
            <p>
              This Section applies only to the extent DTecX is subject to the California Consumer
              Privacy Act (CCPA), as amended, for a particular California consumer or processing
              activity. It supplements the remainder of this Privacy Policy.
            </p>
            <p>
              Depending on the Services used, DTecX may collect or process the following broad
              categories of personal information recognized by California law:
            </p>
            <div className="legal-table-wrap">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Examples Relevant to Rside</th>
                    <th>Sources</th>
                    <th>Purposes / Recipients</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Identifiers</td>
                    <td>Name, email, phone, account IDs, IP address, device/session identifiers.</td>
                    <td>User; Manager User; device/browser; service providers.</td>
                    <td>Account administration, communications, security, support; disclosed to technology providers as needed.</td>
                  </tr>
                  <tr>
                    <td>Customer-record information</td>
                    <td>Contact details and other account/property-management records.</td>
                    <td>User or Manager User.</td>
                    <td>Provide Services, tenant/property management, support, legal compliance.</td>
                  </tr>
                  <tr>
                    <td>Commercial information</td>
                    <td>Subscription records, Rent Payment records/status, transaction records, property-management activity.</td>
                    <td>User; Stripe; Plaid; Manager User.</td>
                    <td>Billing, payments, financial imports, records, support, disputes.</td>
                  </tr>
                  <tr>
                    <td>Internet or electronic-network activity</td>
                    <td>Authentication events, logs, timestamps, device/browser/app information.</td>
                    <td>Devices, apps, servers, authentication providers.</td>
                    <td>Security, operation, troubleshooting, fraud prevention.</td>
                  </tr>
                  <tr>
                    <td>Geolocation-related information</td>
                    <td>Property addresses and mapping information; device location only if a feature requests it and permission is granted.</td>
                    <td>User; device if authorized; mapping providers.</td>
                    <td>Property-management and mapping functionality.</td>
                  </tr>
                  <tr>
                    <td>Professional or business information</td>
                    <td>Manager role, property-management or rental-owner information, organization-related account information.</td>
                    <td>Manager User.</td>
                    <td>Account administration and provision of Manager Services.</td>
                  </tr>
                  <tr>
                    <td>Communications and content</td>
                    <td>Messages, announcements, uploaded documents, photographs, work-order content.</td>
                    <td>Users and Manager Users.</td>
                    <td>Provide communications, storage, sharing, work-order, and support features.</td>
                  </tr>
                  <tr>
                    <td>Sensitive personal information where applicable</td>
                    <td>Account login information, financial-account information made available through payment/banking integrations, and sensitive information that Users choose to include in documents or communications.</td>
                    <td>User; Manager User; Stripe; Plaid; documents.</td>
                    <td>Authentication, requested payment/financial features, document processing, security, legal obligations.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>15.1 California Privacy Rights</h3>
            <p>
              If the CCPA applies, California residents may have rights to know/access, delete,
              correct, opt out of sale or sharing, limit certain uses or disclosures of sensitive
              personal information, and receive non-discriminatory treatment. Additional rights may
              apply to certain automated decision-making technologies under California regulations
              when the statutory and regulatory conditions are met.
            </p>
            <p>
              Rside's current AI lease-extraction feature is designed to extract information for User
              review and is not described as making a solely automated decision that determines tenant
              approval, eviction, rent liability, or another legal or similarly significant result by
              DTecX.
            </p>
            <p>
              California requests may be submitted to{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a>.
            </p>

            {/* ── 16 ── */}
            <h2 id="s16" className="legal-section">16. Additional Information for EEA and UK Individuals</h2>
            <p>
              If the EU General Data Protection Regulation (GDPR), UK GDPR, or related
              data-protection law applies to DTecX as a controller for your personal information,
              you may have rights that include access, rectification, erasure, restriction,
              portability, objection to certain processing, withdrawal of consent where applicable,
              and the right to lodge a complaint with the competent supervisory authority.
            </p>
            <p>
              Where DTecX relies on legitimate interests, those interests may include operating and
              securing the Services, preventing fraud and abuse, supporting Users, maintaining
              reliable systems, and protecting DTecX's legal rights, provided those interests are not
              overridden by applicable individual rights and freedoms.
            </p>
            <p>
              Individuals may contact{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a>{' '}
              to exercise applicable rights or ask questions about international transfers.
            </p>

            {/* ── 17 ── */}
            <h2 id="s17" className="legal-section">17. Information About Tenants Who Have Not Created an Account</h2>
            <p>
              A Manager User may enter personal information about a tenant before that tenant creates
              a Rside-Tenant account. This can include the tenant's name, email address, phone number,
              address, lease information, and documents.
            </p>
            <p>
              DTecX receives that information from the Manager User in order to provide the
              property-management Services requested by the Manager User, maintain records, support
              tenant invitations or account setup, provide authorized document functionality, and
              perform related operational and legal functions described in this Privacy Policy.
            </p>
            <p>
              Manager Users are responsible for having lawful authority to provide this information to
              Rside and for providing any notices or obtaining any permissions required by applicable
              law. A tenant who has not created an account may contact{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a>{' '}
              to ask whether DTecX holds personal information about them and to exercise any privacy
              rights that apply.
            </p>

            {/* ── 18 ── */}
            <h2 id="s18" className="legal-section">18. Children and Users Under 18</h2>
            <p>
              The Services are intended for adults age 18 or older. DTecX does not permit individuals
              under 18 to create or use Rside accounts under the current account policy.
            </p>
            <p>
              If you believe an individual under 18 has created an account or provided personal
              information directly to DTecX in violation of the account requirements, contact{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a>{' '}
              so the situation can be reviewed.
            </p>

            {/* ── 19 ── */}
            <h2 id="s19" className="legal-section">19. Third-Party Services and Privacy Practices</h2>
            <p>
              The Services may rely on or interact with third-party services, including Stripe, Plaid,
              Firebase, Apple, DigitalOcean, Anthropic, Microsoft, Mapbox, financial institutions,
              payment networks, and other technology providers.
            </p>
            <p>
              When a third party independently collects or uses personal information under its own
              terms, that third party's privacy notice governs its independent processing. DTecX does
              not control the privacy practices of independent third parties.
            </p>
            <p>
              Users should review applicable third-party notices, particularly before connecting a
              financial account, establishing a Stripe connected account, initiating a Rent Payment,
              enabling device permissions, or using other integrated functionality.
            </p>

            {/* ── 20 ── */}
            <h2 id="s20" className="legal-section">20. Changes to This Privacy Policy</h2>
            <p>
              DTecX may update this Privacy Policy from time to time to reflect changes in the
              Services, processing activities, vendors, legal requirements, or business practices.
            </p>
            <p>
              The updated Privacy Policy will identify its effective or last-updated date. Where
              required by applicable law, DTecX may provide additional notice or request consent
              before a material change applies to particular processing.
            </p>
            <p>Users should review the Privacy Policy periodically and when notified of a material change.</p>

            {/* ── 21 ── */}
            <h2 id="s21" className="legal-section">21. Contact Us</h2>
            <p>Questions, concerns, or privacy requests may be directed to:</p>
            <p>
              <strong>DTecX LLC</strong><br />
              Rside / Rside-Tenant<br />
              Email:{' '}
              <a href="mailto:support@dtecx.net" className="legal-link">support@dtecx.net</a><br />
              For privacy requests, use the subject line: <strong>Privacy Request</strong>.
            </p>

          </div>
        </div>
      </div>

      <footer className="legal-footer">
        <p>© {new Date().getFullYear()} RSIDE by DTecX LLC. All rights reserved.</p>
        <div className="legal-footer-links">
          <button className="legal-footer-link" onClick={() => navigate('/privacy')}>Privacy Policy</button>
          <span>·</span>
          <button className="legal-footer-link" onClick={() => navigate('/terms')}>Terms of Service</button>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
